import { ApiError, inspectedFetch, type ApiResponse } from '../../core/index.js'
import { datesOf, type SaveShiftPreferencesInput, type ScheduleRun, type ShiftPreferencesView, type SwapCandidate, type SwapRequest, type WorkerBoardView } from '../../schedule/index.js'
import { DEMO_LEAVER_ID, type DemoFacility, type DemoWorker } from './demoData.js'
import type { DemoPlan } from './demoPlan.js'
import { isBlankPlan } from './demoRules.js'
import type { OperatorSession } from './demoOperator.js'

/** How many of the facility's workers save preferences for the demo's run: enough for contests, few enough for the gateway's 30 seconds. */
export const DEMO_RUN_MAX_WORKERS = 8

/**
 * The month the demo builds and releases (scheduling rules plan phase B):
 * at the first active facility, for the workers who are still there, each
 * saving preferences as themselves (a demo session opened for each) so the
 * run has asks to settle - then the operator builds the month taking
 * preferences (the API's `accepting[0]`: with the close enforced, December
 * from about 4 October) and releases
 * it, so the worker seats open on a released schedule and the Tools tab on
 * a run with contests. Null for a blank demo, and for a minimum one, whose month is left for the admin to bid,
 * build and release by hand (the user's call, 20 October 2026).
 */
export function demoRun(plan: DemoPlan): { facility: DemoFacility; workers: DemoWorker[] } | null {
  if (isBlankPlan(plan) || plan.size === 'minimum') {
    return null
  }
  const facility = plan.activeFacilities[0]
  if (!facility) {
    return null
  }
  const workers = plan.workers.filter((worker) => worker.facilityId === facility.facilityId && worker.workerId !== DEMO_LEAVER_ID).slice(0, DEMO_RUN_MAX_WORKERS)
  return workers.length > 0 ? { facility, workers } : null
}

/** Next month, `YYYY-MM`, reckoned in the facility's zone: the fallback when the API answers no month taking preferences (an older API). */
export function demoRunPeriod(facility: Pick<DemoFacility, 'timezone'>, now: Date): string {
  const today = now.toLocaleDateString('en-CA', { timeZone: facility.timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
  const [year, month] = today.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, month ?? 1, 1)).toISOString().slice(0, 7)
}

async function asWorker<T>(apiBaseUrl: string, token: string, method: 'GET' | 'PUT' | 'POST', path: string, body?: unknown): Promise<T> {
  const response = await inspectedFetch(`${apiBaseUrl}${path}`, { method, headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'content-type': 'application/json' }) }, body: body === undefined ? undefined : JSON.stringify(body) })
  let parsed: ApiResponse<T> | null = null
  try {
    parsed = (await response.json()) as ApiResponse<T>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !parsed?.success || parsed.data === undefined) {
    throw new ApiError(parsed?.error ?? `${method} ${path} failed (${response.status})`, response.status)
  }
  return parsed.data
}

/** Saturday or Sunday, from the date alone (UTC noon so no zone shifts the weekday). */
const isWeekend = (date: string): boolean => [0, 6].includes(new Date(`${date}T12:00:00Z`).getUTCDay())
const day = (period: string, dayOfMonth: number): string => `${period}-${String(Math.min(28, Math.max(1, dayOfMonth))).padStart(2, '0')}`

/**
 * The preferences one demo worker asks for, varied by their position on the
 * roster so the run has contests to settle: everyone's hours are their
 * tier's floor to ceiling (the limits say), the shift order rotates, every
 * fourth worker will never work the last type offered, each has one or two
 * High days and a Medium day, and every third asks for a PTO day. Nothing
 * here is chosen by hand: the limits the API answers for the worker decide
 * what may be asked, so a Bronze worker's fixed month and a PRN worker's
 * empty PTO allowance come out right.
 */
export function demoPreferencesFor(view: ShiftPreferencesView, index: number): SaveShiftPreferencesInput | null {
  const { limits, offered, period } = view
  if (!limits.bids || offered.length === 0) {
    return null
  }
  const rotated = [...offered.slice(index % offered.length), ...offered.slice(0, index % offered.length)]
  const neverLast = index % 4 === 3 && rotated.length > 1
  const shiftTypes = rotated.map((entry, position) => ({ shiftType: entry.shiftType, willDo: !(neverLast && position === rotated.length - 1) }))
  const taken = new Set<string>()
  const pick = (candidate: number): string | null => {
    for (let offset = 0; offset < 28; offset += 1) {
      const date = day(period, ((candidate - 1 + offset) % 28) + 1)
      if (!taken.has(date)) {
        taken.add(date)
        return date
      }
    }
    return null
  }
  const daysOff: SaveShiftPreferencesInput['daysOff'] = []
  const highDays = Math.min(limits.maxHighPriorityDays, index % 2 === 0 ? 2 : 1)
  for (let n = 0; n < highDays; n += 1) {
    const date = pick(5 + ((index * 3 + n * 7) % 20))
    if (date) {
      daysOff.push({ date, priority: 'high' })
    }
  }
  const medium = pick(20 + (index % 8))
  if (medium) {
    daysOff.push({ date: medium, priority: 'medium' })
  }
  const ptoAllowed = (limits.ptoDaysPerPeriod ?? 1) > 0 && (view.pto.balanceDays ?? 1) > 0
  const pto = index % 3 === 0 && ptoAllowed ? pick(26 - (index % 5)) : null
  // A part-time or PRN worker marks the days they can work instead of days off (census demo alignment plan A2): every weekend and every other Wednesday.
  const picksAvailableDays = limits.picksAvailableDays ?? false
  const availableDates = picksAvailableDays ? datesOf(period).filter((date, position) => isWeekend(date) || (position % 14 === 2 + (index % 7))) : null
  return {
    period,
    shiftTypes,
    hours: { min: limits.typicalHours.min, max: limits.typicalHours.max },
    ptoDates: pto ? [pto] : [],
    daysOff: picksAvailableDays ? [] : daysOff.sort((a, b) => a.date.localeCompare(b.date)),
    ...(availableDates ? { availableDates } : {}),
    note: index === 0 ? 'Prefer days together where you can.' : null,
    version: view.preferences?.version ?? null,
  }
}

/**
 * The month the facility is taking preferences for, as the API answers a worker: the earliest whose close is still
 * ahead. The demo builds that month, since a month whose close has passed takes no more saves.
 */
export async function openPreferencesPeriod(apiBaseUrl: string, token: string, workerId: string): Promise<string | null> {
  const view = await asWorker<ShiftPreferencesView>(apiBaseUrl, token, 'GET', `/workers/${encodeURIComponent(workerId)}/shift-preferences`)
  return view.accepting?.[0] ?? null
}

/** Saves one worker's preferences for the month as that worker (a demo session's token). False when the worker's tier does not bid. */
export async function savePreferencesAsWorker(apiBaseUrl: string, token: string, workerId: string, period: string, index: number): Promise<boolean> {
  const view = await asWorker<ShiftPreferencesView>(apiBaseUrl, token, 'GET', `/workers/${encodeURIComponent(workerId)}/shift-preferences?period=${encodeURIComponent(period)}`)
  const input = demoPreferencesFor(view, index)
  if (!input) {
    return false
  }
  await asWorker(apiBaseUrl, token, 'PUT', `/workers/${encodeURIComponent(workerId)}/shift-preferences`, input)
  return true
}

async function asOperator<T>(apiBaseUrl: string, session: OperatorSession, path: string, body: unknown): Promise<T> {
  const response = await inspectedFetch(`${apiBaseUrl}${path}`, { method: 'POST', headers: { Authorization: `Bearer ${session.accessToken}`, 'content-type': 'application/json' }, body: JSON.stringify(body) })
  let parsed: ApiResponse<T> | null = null
  try {
    parsed = (await response.json()) as ApiResponse<T>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !parsed?.success || parsed.data === undefined) {
    throw new ApiError(parsed?.error ?? `POST ${path} failed (${response.status})`, response.status)
  }
  return parsed.data
}

/** The reason code the demo releases past a block with: a demo's roster is too small to fill the month, which is the point it shows. */
export const DEMO_RELEASE_REASON = 'DEMO_SETUP'

/**
 * `POST /facilities/{facilityId}/schedule/runs` then `.../release`, as the operator: the month built from everyone's ask and
 * released to them. A demo's few workers leave more than a fifth of the month open, which the release guard blocks
 * (facility operations dashboard plan §6), so a blocked release goes past the block with a reason code, as a person would.
 */
export async function buildAndReleaseAsOperator(apiBaseUrl: string, session: OperatorSession, facilityId: string, period: string): Promise<ScheduleRun> {
  const built = await asOperator<ScheduleRun>(apiBaseUrl, session, `/facilities/${encodeURIComponent(facilityId)}/schedule/runs`, { period })
  const release = `/facilities/${encodeURIComponent(facilityId)}/schedule/runs/${encodeURIComponent(built.runId)}/release`
  try {
    return await asOperator<ScheduleRun>(apiBaseUrl, session, release, {})
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 409) {
      return asOperator<ScheduleRun>(apiBaseUrl, session, release, { override: { reasonCode: DEMO_RELEASE_REASON, note: 'Released by demo onboarding' } })
    }
    throw error
  }
}

/** A swap the rules hold for approval, so the Today tab opens with something to decide (facility operations dashboard plan phase 5). */
export interface DemoSwapForApproval {
  requester: string
  peer: string
  date: string
}

/**
 * Seeds the Today tab's Needs you (plan phase 5): on the released month, a worker offers one of their shifts to a
 * peer of another tier and the peer accepts - a swap the rules hold for the facility administrator's approval. Each
 * acts as themselves through `openSession` (a demo session). Tries the workers in turn and stops at the first that
 * works; null when none could (a month with no shift a peer of another tier may take), which leaves the step done.
 */
export async function seedSwapForApproval(apiBaseUrl: string, openSession: (workerId: string) => Promise<string>, workers: readonly { workerId: string; name: string }[], period: string): Promise<DemoSwapForApproval | null> {
  const query = `?period=${encodeURIComponent(period)}`
  for (const worker of workers) {
    const token = await openSession(worker.workerId)
    const board = await asWorker<WorkerBoardView>(apiBaseUrl, token, 'GET', `/workers/${encodeURIComponent(worker.workerId)}/schedule/board${query}`).catch(() => null)
    const ownTier = (await asWorker<{ tier: string }>(apiBaseUrl, token, 'GET', `/workers/${encodeURIComponent(worker.workerId)}/standing`).catch(() => null))?.tier ?? null
    // The latest shifts first: the trade board is open until a week before a shift.
    for (const shift of [...(board?.assignments ?? [])].reverse().slice(0, 6)) {
      const candidates = await asWorker<SwapCandidate[]>(apiBaseUrl, token, 'GET', `/workers/${encodeURIComponent(worker.workerId)}/schedule/swaps/candidates?shiftId=${encodeURIComponent(shift.shiftId)}&period=${encodeURIComponent(period)}`).catch(() => [])
      const peer = candidates.find((candidate) => ownTier !== null && candidate.tier !== ownTier && candidate.theirShiftId === null)
      if (!peer) {
        continue
      }
      const swap = await asWorker<SwapRequest>(apiBaseUrl, token, 'POST', `/workers/${encodeURIComponent(worker.workerId)}/schedule/swaps${query}`, { shiftId: shift.shiftId, withWorkerId: peer.workerId }).catch(() => null)
      if (!swap) {
        continue
      }
      const peerToken = await openSession(peer.workerId)
      const answered = await asWorker<SwapRequest>(apiBaseUrl, peerToken, 'POST', `/workers/${encodeURIComponent(peer.workerId)}/schedule/swaps/${encodeURIComponent(swap.swapId)}/answer${query}`, { answer: 'accept' }).catch(() => null)
      if (answered?.status === 'needs_approval') {
        return { requester: worker.name, peer: peer.name, date: shift.date }
      }
    }
  }
  return null
}
